"use client";

import { Reveal } from "@/components/ui/Reveal";

export type LegacyCard = {
  title: string;
  description: string;
  tone: "brown" | "green";
  width: "narrow" | "wide";
  reveal: "left" | "right";
};

const defaultCards: LegacyCard[] = [
  {
    title: "Guidance at Every Step",
    description:
      "We stay with you through every chapter, ensuring clarity, confidence, and seamless progress for your book.",
    tone: "brown",
    width: "narrow",
    reveal: "right",
  },
  {
    title: "Experts Who Listen",
    description:
      "Our team of specialists actively listens to your goals, challenges, and ideas, offering precise guidance that brings your story fully to life.",
    tone: "green",
    width: "wide",
    reveal: "left",
  },
  {
    title: "Results You Trust",
    description:
      "12+ years of helping authors achieve publication success proves our commitment, reliability, and ability to turn dreams into published, celebrated books.",
    tone: "green",
    width: "wide",
    reveal: "right",
  },
  {
    title: "Designed With Care",
    description:
      "Every manuscript is treated uniquely, blending editorial expertise with a deep respect for your vision.",
    tone: "brown",
    width: "narrow",
    reveal: "left",
  },
];

export type MoreThanPublisherSectionProps = {
  eyebrow?: string;
  heading?: string;
  description?: string;
  cards?: LegacyCard[];
  className?: string;
};

export function MoreThanPublisherSection({
  eyebrow = "More Than a Publisher",
  heading = "We Believe Your Story is a Legacy Waiting to be Told!",
  description = "In a world with thousands of book publishing companies in the States, we offer a different path. Our integrated book editing and publishing services are designed to guide authors from manuscripts to finished books seamlessly. With no shortcuts or generic writing, collaborate with us to turn your ideas into an exceptional book you will be proud of.",
  cards = defaultCards,
  className,
}: MoreThanPublisherSectionProps) {
  return (
    <section
      data-feather-section="morethan"
      className={["bg-white py-12 lg:py-[50px]", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-5xl text-center">
          <Reveal variant="zoom">
            <p className="font-heading text-xl font-semibold tracking-wide text-[#29361a] sm:text-[28px] sm:leading-9">
              {eyebrow}
            </p>
          </Reveal>

          <Reveal variant="zoom" delayMs={120}>
            <h2 className="mt-2 font-heading text-3xl font-bold text-[#b46c29] sm:text-4xl lg:whitespace-nowrap lg:text-[45px] lg:leading-[54px]">
              {heading}
            </h2>
          </Reveal>

          <Reveal variant="zoom" delayMs={220}>
            <p className="mx-auto mt-4 max-w-4xl text-base leading-relaxed text-gray-700">
              {description}
            </p>
          </Reveal>
        </div>

        <div className="grid gap-5 md:grid-cols-12 md:gap-6">
          {cards.map((card, index) => (
            <Reveal
              key={card.title}
              variant={card.reveal}
              delayMs={index * 80}
              className={
                card.width === "narrow"
                  ? "md:col-span-5"
                  : "md:col-span-7"
              }
            >
              <article
                className={[
                  "choose-card rounded-[7px] border-2 border-transparent p-5 transition-colors duration-300 sm:p-6",
                  card.tone === "brown"
                    ? "choose-card-brown bg-[#b46c29] text-white hover:border-[#b46c29]"
                    : "choose-card-green bg-[#29361a] text-white hover:border-[#b46c29]",
                ].join(" ")}
              >
                <h3 className="font-heading text-xl font-bold sm:text-2xl">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/95 sm:text-base">
                  {card.description}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
